import type { CSSObject, Theme } from '@mui/material/styles';

import { varAlpha } from 'minimal-shared/utils';

/**
 * Subtle 1px inset outline for content images and avatars. Pure black on light,
 * pure white on dark — never a tinted neutral, which would pick up the surface
 * colour and read as dirt on the image edge. `outline-offset: -1px` keeps it
 * inset so the image stays its intended size. Modern browsers render `outline`
 * following `border-radius`, so this works on rounded/circular images too.
 */
export function imageOutlineSx(theme: Theme): CSSObject {
  return {
    outline: `1px solid ${varAlpha(theme.palette.common.blackChannel, 0.1)}`,
    outlineOffset: '-1px',
    ...theme.applyStyles('dark', {
      outline: `1px solid ${varAlpha(theme.palette.common.whiteChannel, 0.1)}`,
    }),
  } as const;
}

/**
 * A black pill with a gradient ring, for a contained primary Button. The
 * contained primary fill stays the loud option; this is the quieter one.
 */
export function gradientBorderSx(theme: Theme): CSSObject {
  const fill = `linear-gradient(${theme.palette.common.black}, ${theme.palette.common.black}) padding-box`;

  return {
    borderRadius: '50px',
    color: theme.palette.common.white,
    boxShadow: 'none',
    border: '1.5px solid transparent',
    background: `${fill}, ${theme.gradients.primary.background} border-box`,
    '&::before': { display: 'none' },
    '&:hover': {
      boxShadow: 'none',
      background: `${fill}, ${theme.gradients.primary.hover} border-box`,
    },
    '&.Mui-disabled': {
      color: theme.palette.action.disabled,
      background: `${fill}, linear-gradient(${theme.palette.action.disabledBackground}, ${theme.palette.action.disabledBackground}) border-box`,
    },
  };
}
